import { useState, useEffect } from 'preact/hooks'
import { useForm } from 'react-hook-form'
import { X, Loader2, AlertCircle, Plus, Trash2 } from 'lucide-preact'
import { AUTH_TYPE } from '@/types/authConnections'
import type { AuthenticationType, AddAuthConnectionRequest, GoogleWorkspaceCredentials } from '@/types/authConnections'

interface GoogleWorkspaceFormData {
  clientId: string
  clientSecret: string
  domain: string
}

interface AuthConnectionModalProps {
  isOpen: boolean
  authType: AuthenticationType | null
  typeLabel?: string
  allowedDomains?: string[]
  onClose: () => void
  onCreate: (request: AddAuthConnectionRequest) => Promise<void>
}

export function AuthConnectionModal({
  isOpen,
  authType,
  typeLabel,
  allowedDomains = [],
  onClose,
  onCreate
}: AuthConnectionModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [domainAliases, setDomainAliases] = useState<string[]>([])
  const [newAlias, setNewAlias] = useState('')

  const requiresCredentials = authType === AUTH_TYPE.GoogleWorkspace

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<GoogleWorkspaceFormData>({
    mode: 'onChange',
    defaultValues: {
      clientId: '',
      clientSecret: '',
      domain: ''
    }
  })

  useEffect(() => {
    if (isOpen && authType === AUTH_TYPE.GoogleWorkspace) {
      // Pre-fill domain if only one allowed
      if (allowedDomains.length === 1) {
        setValue('domain', allowedDomains[0])
      }
    }
  }, [isOpen, authType, allowedDomains, setValue])

  const resetFormState = () => {
    reset()
    setDomainAliases([])
    setNewAlias('')
    setError(null)
    setLoading(false)
  }

  const handleClose = () => {
    if (!loading) {
      resetFormState()
      onClose()
    }
  }

  const validateDomainAliases = (): string | null => {
    for (const alias of domainAliases) {
      if (alias.length < 3 || alias.length > 253) {
        return 'Each domain alias must be between 3 and 253 characters'
      }
      if (!alias.includes('.')) {
        return 'Each domain alias must contain a dot (.)'
      }
    }
    return null
  }

  const onSubmit = async (data: GoogleWorkspaceFormData) => {
    setError(null)

    // Validate domain aliases
    const aliasError = validateDomainAliases()
    if (aliasError) {
      setError(aliasError)
      return
    }

    const credentials: GoogleWorkspaceCredentials = {
      clientId: data.clientId.trim(),
      clientSecret: data.clientSecret.trim(),
      domain: data.domain.trim(),
      domainAliases: domainAliases.length > 0 ? domainAliases : undefined
    }

    setLoading(true)
    try {
      await onCreate({
        type: AUTH_TYPE.GoogleWorkspace,
        googleWorkspace: credentials
      })
      resetFormState()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create connection')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateSimple = async () => {
    setError(null)
    setLoading(true)
    try {
      await onCreate({ type: authType! })
      resetFormState()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create connection')
    } finally {
      setLoading(false)
    }
  }

  const addDomainAlias = () => {
    const trimmed = newAlias.trim()
    if (trimmed && !domainAliases.includes(trimmed)) {
      setDomainAliases([...domainAliases, trimmed])
      setNewAlias('')
    }
  }

  const removeDomainAlias = (alias: string) => {
    setDomainAliases(domainAliases.filter(a => a !== alias))
  }

  if (!isOpen || authType === null) return null

  const displayLabel = typeLabel || authType

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Configure {displayLabel}
          </h3>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!requiresCredentials && (
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {authType === AUTH_TYPE.GoogleSocial ? (
                  <>No configuration required. Click <strong>Enable</strong> to allow users to sign in with any Google account.</>
                ) : (
                  <>No configuration required. Click <strong>Enable</strong> to allow email and password authentication. You can customize branding in the next step.</>
                )}
              </p>
            </div>
          )}

          {authType === AUTH_TYPE.GoogleWorkspace && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">Before you begin:</p>
                <p>Create an OAuth 2.0 application in your Google Admin Console and obtain the Client ID and Secret.</p>
                <a
                  href="https://support.google.com/cloud/answer/6158849"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline mt-1 inline-block"
                >
                  View setup instructions →
                </a>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Client ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('clientId', {
                    required: 'Client ID is required',
                    minLength: {
                      value: 10,
                      message: 'Client ID must be at least 10 characters'
                    },
                    maxLength: {
                      value: 500,
                      message: 'Client ID must not exceed 500 characters'
                    }
                  })}
                  disabled={loading}
                  className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.clientId ? 'border-red-400' : 'border-slate-300 dark:border-slate-600'
                  }`}
                  placeholder="123456789-abc.apps.googleusercontent.com"
                />
                {errors.clientId && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.clientId.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Client Secret <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  {...register('clientSecret', {
                    required: 'Client Secret is required',
                    minLength: {
                      value: 10,
                      message: 'Client Secret must be at least 10 characters'
                    },
                    maxLength: {
                      value: 500,
                      message: 'Client Secret must not exceed 500 characters'
                    }
                  })}
                  disabled={loading}
                  className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.clientSecret ? 'border-red-400' : 'border-slate-300 dark:border-slate-600'
                  }`}
                  placeholder="Enter client secret"
                />
                {errors.clientSecret && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.clientSecret.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Domain <span className="text-red-500">*</span>
                </label>
                {allowedDomains.length > 1 ? (
                  <select
                    {...register('domain', {
                      required: 'Domain is required',
                      minLength: {
                        value: 3,
                        message: 'Domain must be at least 3 characters'
                      },
                      maxLength: {
                        value: 253,
                        message: 'Domain must not exceed 253 characters'
                      },
                      pattern: {
                        value: /\./,
                        message: 'Domain must contain a dot (.)'
                      }
                    })}
                    disabled={loading}
                    className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.domain ? 'border-red-400' : 'border-slate-300 dark:border-slate-600'
                    }`}
                  >
                    <option value="">Select domain</option>
                    {allowedDomains.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    {...register('domain', {
                      required: 'Domain is required',
                      minLength: {
                        value: 3,
                        message: 'Domain must be at least 3 characters'
                      },
                      maxLength: {
                        value: 253,
                        message: 'Domain must not exceed 253 characters'
                      },
                      pattern: {
                        value: /\./,
                        message: 'Domain must contain a dot (.)'
                      }
                    })}
                    disabled={loading || allowedDomains.length === 1}
                    className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.domain ? 'border-red-400' : 'border-slate-300 dark:border-slate-600'
                    }`}
                    placeholder="example.com"
                  />
                )}
                {errors.domain && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.domain.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Domain Aliases (Optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newAlias}
                    onInput={e => setNewAlias((e.target as HTMLInputElement).value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addDomainAlias()
                      }
                    }}
                    disabled={loading}
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="alias.example.com"
                  />
                  <button
                    type="button"
                    onClick={addDomainAlias}
                    disabled={loading || !newAlias.trim()}
                    className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {domainAliases.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {domainAliases.map(alias => (
                      <div
                        key={alias}
                        className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700"
                      >
                        <span className="text-sm text-slate-700 dark:text-slate-300">{alias}</span>
                        <button
                          type="button"
                          onClick={() => removeDomainAlias(alias)}
                          disabled={loading}
                          className="text-slate-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={requiresCredentials ? handleSubmit(onSubmit) : handleCreateSimple}
            disabled={loading}
            className="px-5 py-2 text-sm font-semibold rounded-lg bg-primary-600 hover:bg-primary-700 text-white flex items-center gap-2 disabled:opacity-50"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {requiresCredentials ? 'Create Connection' : 'Enable Method'}
          </button>
        </div>
      </div>
    </div>
  )
}
